import React, { useEffect, useState } from 'react';
import { Navbar } from '../../components/Navbar';
import { Footer } from '../../components/Footer';
import { PencilIcon, FileTextIcon, ArrowRightIcon, CheckIcon, XIcon, UserIcon } from 'lucide-react';
export const ProductDescriptionsPage = () => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    window.scrollTo(0, 0);
    const handleScroll = () => {
      const isScrolled = window.scrollY > 50;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [scrolled]);
  return <div className="w-full bg-gray-50 dark:bg-[#0A1C1A] min-h-screen" data-id="element-6119">
      <Navbar scrolled={scrolled} data-id="element-6120" />
      <main className="pt-24 pb-20" data-id="element-6121">
        <div className="container mx-auto px-4 md:px-6" data-id="element-6122">
          <div className="max-w-4xl mx-auto" data-id="element-6123">
            {/* Header */}
            <div className="bg-gradient-to-r from-[#0D3C45] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] rounded-xl p-8 md:p-12 mb-12 text-white relative overflow-hidden" data-id="element-6124">
              <div className="absolute right-0 bottom-0 opacity-10" data-id="element-6125">
                <PencilIcon className="h-64 w-64" data-id="element-6126" />
              </div>
              <div className="relative z-10" data-id="element-6127">
                <h1 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-6128">
                  Creating Compelling Product Descriptions
                </h1>
                <p className="text-xl opacity-90 max-w-3xl" data-id="element-6129">
                  Learn how to write product descriptions that convert browsers
                  into buyers and effectively communicate the value of your
                  digital products.
                </p>
              </div>
            </div>
            {/* Main content */}
            <div className="bg-white dark:bg-[#162825] rounded-xl p-8 md:p-10 shadow-sm mb-10" data-id="element-6130">
              <h2 className="text-2xl font-bold text-gray-800 dark:text-[#F8FAF9] mb-6" data-id="element-6131">
                Why Product Descriptions Matter
              </h2>
              <p className="text-gray-600 dark:text-[#B0C1BD] mb-6" data-id="element-6132">
                Your product description is often the deciding factor in whether
                a potential customer makes a purchase. It's your chance to
                communicate value, overcome objections, and create desire for
                your digital product.
              </p>
              <p className="text-gray-600 dark:text-[#B0C1BD] mb-6" data-id="element-6133">
                In a marketplace crowded with options, a well-crafted
                description helps your product stand out and connects with your
                ideal customer on both rational and emotional levels.
              </p>
              <div className="border-l-4 border-[#2DB489] dark:border-[#4ED1A0] pl-6 my-8 py-2" data-id="element-6134">
                <p className="text-lg font-medium text-gray-800 dark:text-[#F8FAF9] italic" data-id="element-6135">
                  "The best product descriptions address specific customer pain
                  points and clearly articulate how your product solves them
                  better than any alternative."
                </p>
              </div>
              <h2 className="text-2xl font-bold text-gray-800 dark:text-[#F8FAF9] mt-12 mb-6" data-id="element-6136">
                The Anatomy of an Effective Product Description
              </h2>
              <div className="mb-8" data-id="element-6137">
                <h3 className="text-xl font-semibold text-gray-800 dark:text-[#F8FAF9] mb-4 flex items-center" data-id="element-6138">
                  <span className="bg-[#2DB489] dark:bg-[#4ED1A0] text-white w-8 h-8 rounded-full flex items-center justify-center mr-3" data-id="element-6139">
                    1
                  </span>
                  Compelling Headline
                </h3>
                <p className="text-gray-600 dark:text-[#B0C1BD] mb-4 ml-11" data-id="element-6140">
                  Your headline should instantly communicate what your product
                  is and its primary benefit. It's the first thing customers
                  see, so make it count.
                </p>
                <div className="ml-11 bg-gray-50 dark:bg-[#1A3330] p-5 rounded-lg mb-6" data-id="element-6141">
                  <div className="flex items-start mb-4" data-id="element-6142">
                    <XIcon className="h-5 w-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-6143" />
                    <div data-id="element-6144">
                      <p className="font-medium text-gray-800 dark:text-[#F8FAF9] mb-1" data-id="element-6145">
                        Weak Example:
                      </p>
                      <p className="text-gray-600 dark:text-[#B0C1BD]" data-id="element-6146">
                        "UI Kit for Websites"
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start" data-id="element-6147">
                    <CheckIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-6148" />
                    <div data-id="element-6149">
                      <p className="font-medium text-gray-800 dark:text-[#F8FAF9] mb-1" data-id="element-6150">
                        Strong Example:
                      </p>
                      <p className="text-gray-600 dark:text-[#B0C1BD]" data-id="element-6151">
                        "Professional E-commerce UI Kit: Launch Your Online
                        Store in Half the Time"
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mb-8" data-id="element-6152">
                <h3 className="text-xl font-semibold text-gray-800 dark:text-[#F8FAF9] mb-4 flex items-center" data-id="element-6153">
                  <span className="bg-[#2DB489] dark:bg-[#4ED1A0] text-white w-8 h-8 rounded-full flex items-center justify-center mr-3" data-id="element-6154">
                    2
                  </span>
                  Benefits-Focused Opening
                </h3>
                <p className="text-gray-600 dark:text-[#B0C1BD] mb-4 ml-11" data-id="element-6155">
                  Start with the most compelling benefits, not just features.
                  Explain how your product will improve the customer's life or
                  work.
                </p>
                <div className="ml-11 bg-gray-50 dark:bg-[#1A3330] p-5 rounded-lg mb-6" data-id="element-6156">
                  <div className="flex items-start mb-4" data-id="element-6157">
                    <XIcon className="h-5 w-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-6158" />
                    <div data-id="element-6159">
                      <p className="font-medium text-gray-800 dark:text-[#F8FAF9] mb-1" data-id="element-6160">
                        Feature-focused (Weak):
                      </p>
                      <p className="text-gray-600 dark:text-[#B0C1BD]" data-id="element-6161">
                        "This template includes 50+ components, 10 page layouts,
                        and responsive design."
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start" data-id="element-6162">
                    <CheckIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-6163" />
                    <div data-id="element-6164">
                      <p className="font-medium text-gray-800 dark:text-[#F8FAF9] mb-1" data-id="element-6165">
                        Benefit-focused (Strong):
                      </p>
                      <p className="text-gray-600 dark:text-[#B0C1BD]" data-id="element-6166">
                        "Launch your professional website in hours instead of
                        weeks with our comprehensive template that makes you
                        look like you hired a top design agency – without the
                        agency price tag."
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mb-8" data-id="element-6167">
                <h3 className="text-xl font-semibold text-gray-800 dark:text-[#F8FAF9] mb-4 flex items-center" data-id="element-6168">
                  <span className="bg-[#2DB489] dark:bg-[#4ED1A0] text-white w-8 h-8 rounded-full flex items-center justify-center mr-3" data-id="element-6169">
                    3
                  </span>
                  Scannable Format
                </h3>
                <p className="text-gray-600 dark:text-[#B0C1BD] mb-4 ml-11" data-id="element-6170">
                  Most customers scan rather than read every word. Use bullet
                  points, subheadings, and short paragraphs to make your
                  description easy to digest.
                </p>
                <div className="ml-11 bg-gray-50 dark:bg-[#1A3330] p-5 rounded-lg mb-6" data-id="element-6171">
                  <p className="font-medium text-gray-800 dark:text-[#F8FAF9] mb-3" data-id="element-6172">
                    Effective Format Example:
                  </p>
                  <div className="space-y-4" data-id="element-6173">
                    <div data-id="element-6174">
                      <p className="font-medium text-gray-700 dark:text-[#C4D1CC]" data-id="element-6175">
                        ✨ Save Countless Design Hours
                      </p>
                      <p className="text-gray-600 dark:text-[#B0C1BD] text-sm ml-6" data-id="element-6176">
                        Get your product to market faster with our pre-designed
                        components.
                      </p>
                    </div>
                    <div data-id="element-6177">
                      <p className="font-medium text-gray-700 dark:text-[#C4D1CC]" data-id="element-6178">
                        🔍 Impress Your Clients
                      </p>
                      <p className="text-gray-600 dark:text-[#B0C1BD] text-sm ml-6" data-id="element-6179">
                        Present polished, professional designs that look
                        custom-made.
                      </p>
                    </div>
                    <div data-id="element-6180">
                      <p className="font-medium text-gray-700 dark:text-[#C4D1CC]" data-id="element-6181">
                        💰 Increase Your Profit Margins
                      </p>
                      <p className="text-gray-600 dark:text-[#B0C1BD] text-sm ml-6" data-id="element-6182">
                        Charge premium rates while reducing your production
                        time.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <h2 className="text-2xl font-bold text-gray-800 dark:text-[#F8FAF9] mt-12 mb-6" data-id="element-6183">
                Advanced Techniques for Digital Product Descriptions
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10" data-id="element-6184">
                <div className="bg-white dark:bg-[#162825] border border-gray-200 dark:border-[#1A3330] rounded-xl p-6 shadow-sm" data-id="element-6185">
                  <div className="flex items-center mb-4" data-id="element-6186">
                    <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center mr-3" data-id="element-6187">
                      <UserIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" data-id="element-6188" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-800 dark:text-[#F8FAF9]" data-id="element-6189">
                      Customer-Centered Language
                    </h3>
                  </div>
                  <p className="text-gray-600 dark:text-[#B0C1BD] mb-4" data-id="element-6190">
                    Use "you" and "your" more than "we" and "our." Focus on the
                    customer's experience rather than your process.
                  </p>
                  <div className="bg-gray-50 dark:bg-[#1A3330] p-3 rounded-lg" data-id="element-6191">
                    <p className="text-sm text-gray-600 dark:text-[#B0C1BD]" data-id="element-6192">
                      <span className="font-medium text-gray-700 dark:text-[#C4D1CC]" data-id="element-6193">
                        Instead of:
                      </span>{' '}
                      "We designed this template with attention to detail."
                    </p>
                    <p className="text-sm text-gray-600 dark:text-[#B0C1BD] mt-2" data-id="element-6194">
                      <span className="font-medium text-gray-700 dark:text-[#C4D1CC]" data-id="element-6195">
                        Try:
                      </span>{' '}
                      "You'll impress clients with the thoughtful details in
                      every screen of your design."
                    </p>
                  </div>
                </div>
                <div className="bg-white dark:bg-[#162825] border border-gray-200 dark:border-[#1A3330] rounded-xl p-6 shadow-sm" data-id="element-6196">
                  <div className="flex items-center mb-4" data-id="element-6197">
                    <div className="w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center mr-3" data-id="element-6198">
                      <FileTextIcon className="h-5 w-5 text-purple-600 dark:text-purple-400" data-id="element-6199" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-800 dark:text-[#F8FAF9]" data-id="element-6200">
                      Storytelling Elements
                    </h3>
                  </div>
                  <p className="text-gray-600 dark:text-[#B0C1BD] mb-4" data-id="element-6201">
                    Incorporate mini-stories about how your product can be used
                    in real-world scenarios.
                  </p>
                  <div className="bg-gray-50 dark:bg-[#1A3330] p-3 rounded-lg" data-id="element-6202">
                    <p className="text-sm text-gray-600 dark:text-[#B0C1BD]" data-id="element-6203">
                      "Imagine presenting your client with a complete website
                      prototype in your very first meeting. Watch their eyes
                      light up as you scroll through perfectly designed pages
                      that look custom-built for their business..."
                    </p>
                  </div>
                </div>
              </div>
              <div className="border-t border-gray-200 dark:border-[#1A3330] pt-10 mt-10" data-id="element-6204">
                <h2 className="text-2xl font-bold text-gray-800 dark:text-[#F8FAF9] mb-6" data-id="element-6205">
                  Before and After Example
                </h2>
                <div className="bg-gray-50 dark:bg-[#1A3330] rounded-xl overflow-hidden mb-8" data-id="element-6206">
                  <div className="bg-red-50 dark:bg-red-900/20 p-6 border-b border-gray-200 dark:border-[#1A3330]" data-id="element-6207">
                    <h3 className="font-medium text-gray-800 dark:text-[#F8FAF9] mb-3 flex items-center" data-id="element-6208">
                      <XIcon className="h-5 w-5 text-red-500 mr-2" data-id="element-6209" />
                      Before: Weak Product Description
                    </h3>
                    <p className="text-gray-600 dark:text-[#B0C1BD]" data-id="element-6210">
                      "Notion Template Pack includes 10 templates for
                      productivity and business. Works on all devices. Easy to
                      customize. Includes dashboard, task management, CRM,
                      notes, project management, and more. Save time with these
                      templates."
                    </p>
                  </div>
                  <div className="bg-green-50 dark:bg-green-900/20 p-6" data-id="element-6211">
                    <h3 className="font-medium text-gray-800 dark:text-[#F8FAF9] mb-3 flex items-center" data-id="element-6212">
                      <CheckIcon className="h-5 w-5 text-green-500 mr-2" data-id="element-6213" />
                      After: Strong Product Description
                    </h3>
                    <div className="space-y-4" data-id="element-6214">
                      <p className="text-gray-600 dark:text-[#B0C1BD] font-medium text-lg" data-id="element-6215">
                        Transform Your Productivity: The Ultimate Notion
                        Workspace for Busy Professionals
                      </p>
                      <p className="text-gray-600 dark:text-[#B0C1BD]" data-id="element-6216">
                        Stop wasting hours setting up your workspace. Get
                        instantly organized with our premium Notion template
                        pack designed specifically for freelancers and small
                        businesses.
                      </p>
                      <div data-id="element-6217">
                        <p className="font-medium text-gray-700 dark:text-[#C4D1CC]" data-id="element-6218">
                          🚀 Reclaim 5+ Hours Every Week
                        </p>
                        <p className="text-gray-600 dark:text-[#B0C1BD] text-sm ml-6" data-id="element-6219">
                          Track projects, manage clients, and organize tasks in
                          one beautiful, connected system.
                        </p>
                      </div>
                      <div data-id="element-6220">
                        <p className="font-medium text-gray-700 dark:text-[#C4D1CC]" data-id="element-6221">
                          💼 Impress Clients with Professional Organization
                        </p>
                        <p className="text-gray-600 dark:text-[#B0C1BD] text-sm ml-6" data-id="element-6222">
                          Never miss a deadline or lose an important document
                          again.
                        </p>
                      </div>
                      <div data-id="element-6223">
                        <p className="font-medium text-gray-700 dark:text-[#C4D1CC]" data-id="element-6224">
                          🔄 Works Seamlessly Across All Your Devices
                        </p>
                        <p className="text-gray-600 dark:text-[#B0C1BD] text-sm ml-6" data-id="element-6225">
                          Access your workspace from desktop, tablet, or mobile
                          with our responsive design.
                        </p>
                      </div>
                      <p className="text-gray-600 dark:text-[#B0C1BD] italic" data-id="element-6226">
                        "This template saved me from spreadsheet hell! My client
                        onboarding is now completely streamlined, and I look so
                        much more professional." — Sarah K., Freelance Designer
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-[#0D3C45]/5 dark:bg-[#0D3C45]/20 rounded-xl p-6 mt-12" data-id="element-6227">
                <h2 className="text-xl font-bold text-gray-800 dark:text-[#F8FAF9] mb-4" data-id="element-6228">
                  Key Takeaways
                </h2>
                <ul className="space-y-3" data-id="element-6229">
                  <li className="flex items-start" data-id="element-6230">
                    <div className="bg-[#2DB489] dark:bg-[#4ED1A0] rounded-full p-1 mr-3 mt-1 flex-shrink-0" data-id="element-6231">
                      <CheckIcon className="h-3 w-3 text-white" data-id="element-6232" />
                    </div>
                    <p className="text-gray-700 dark:text-[#B0C1BD]" data-id="element-6233">
                      Focus on benefits over features—explain how your product
                      solves specific problems
                    </p>
                  </li>
                  <li className="flex items-start" data-id="element-6234">
                    <div className="bg-[#2DB489] dark:bg-[#4ED1A0] rounded-full p-1 mr-3 mt-1 flex-shrink-0" data-id="element-6235">
                      <CheckIcon className="h-3 w-3 text-white" data-id="element-6236" />
                    </div>
                    <p className="text-gray-700 dark:text-[#B0C1BD]" data-id="element-6237">
                      Make your description scannable with clear headings,
                      bullet points, and short paragraphs
                    </p>
                  </li>
                  <li className="flex items-start" data-id="element-6238">
                    <div className="bg-[#2DB489] dark:bg-[#4ED1A0] rounded-full p-1 mr-3 mt-1 flex-shrink-0" data-id="element-6239">
                      <CheckIcon className="h-3 w-3 text-white" data-id="element-6240" />
                    </div>
                    <p className="text-gray-700 dark:text-[#B0C1BD]" data-id="element-6241">
                      Use customer-centered language that speaks directly to
                      your ideal buyer
                    </p>
                  </li>
                  <li className="flex items-start" data-id="element-6242">
                    <div className="bg-[#2DB489] dark:bg-[#4ED1A0] rounded-full p-1 mr-3 mt-1 flex-shrink-0" data-id="element-6243">
                      <CheckIcon className="h-3 w-3 text-white" data-id="element-6244" />
                    </div>
                    <p className="text-gray-700 dark:text-[#B0C1BD]" data-id="element-6245">
                      Include social proof like testimonials to build trust and
                      credibility
                    </p>
                  </li>
                </ul>
              </div>
            </div>
            {/* Related resources */}
            <div className="bg-white dark:bg-[#162825] rounded-xl p-8 shadow-sm mb-10" data-id="element-6246">
              <h2 className="text-xl font-bold text-gray-800 dark:text-[#F8FAF9] mb-6" data-id="element-6247">
                Related Resources
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-6248">
                <a href="/learn/listing-checklist" className="group block bg-gray-50 dark:bg-[#1A3330] rounded-lg p-5 transition-all hover:shadow-md" data-id="element-6249">
                  <h3 className="font-semibold text-gray-800 dark:text-[#F8FAF9] mb-2 group-hover:text-[#2DB489] dark:group-hover:text-[#4ED1A0] transition-colors" data-id="element-6250">
                    Product Listing Checklist
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-[#B0C1BD] mb-3" data-id="element-6251">
                    A comprehensive checklist to ensure your product listings
                    are complete and optimized.
                  </p>
                  <div className="flex items-center text-[#2DB489] dark:text-[#4ED1A0] text-sm font-medium" data-id="element-6252">
                    <span data-id="element-6253">Read more</span>
                    <ArrowRightIcon className="ml-1 h-4 w-4 transform group-hover:translate-x-1 transition-transform" data-id="element-6254" />
                  </div>
                </a>
                <a href="/learn/product-screenshots" className="group block bg-gray-50 dark:bg-[#1A3330] rounded-lg p-5 transition-all hover:shadow-md" data-id="element-6255">
                  <h3 className="font-semibold text-gray-800 dark:text-[#F8FAF9] mb-2 group-hover:text-[#2DB489] dark:group-hover:text-[#4ED1A0] transition-colors" data-id="element-6256">
                    Creating Perfect Product Screenshots
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-[#B0C1BD] mb-3" data-id="element-6257">
                    Learn how to create professional screenshots that showcase
                    your digital products.
                  </p>
                  <div className="flex items-center text-[#2DB489] dark:text-[#4ED1A0] text-sm font-medium" data-id="element-6258">
                    <span data-id="element-6259">Read more</span>
                    <ArrowRightIcon className="ml-1 h-4 w-4 transform group-hover:translate-x-1 transition-transform" data-id="element-6260" />
                  </div>
                </a>
              </div>
            </div>
            {/* CTA */}
            <div className="bg-gradient-to-r from-[#0D3C45] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] rounded-xl p-8 text-white text-center" data-id="element-6261">
              <h2 className="text-2xl font-bold mb-4" data-id="element-6262">
                Ready to write better product descriptions?
              </h2>
              <p className="text-lg opacity-90 mb-6 max-w-2xl mx-auto" data-id="element-6263">
                Apply these techniques to your digital products and start
                converting more browsers into buyers.
              </p>
              <a href="/sell-product" className="inline-block bg-white text-[#0D3C45] px-6 py-3 rounded-lg font-medium hover:bg-opacity-90 transition-all" data-id="element-6264">
                Start Selling
              </a>
            </div>
          </div>
        </div>
      </main>
      <Footer data-id="element-6265" />
    </div>;
};